import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Layout from "../../components/Layout/Layout";
import Sidebar from "../../components/Dashboard/Sidebar";
import { useAuth } from "../../context/auth";
import { useNavigate } from "react-router-dom";

import {
  FiImage,
  FiPlus,
  FiTrash2,
  FiEdit,
  FiUpload,
  FiX,
} from "react-icons/fi";

const AdminBanner = () => {
  const navigate = useNavigate();
  const [auth, setAuth] = useAuth();

  const headers = {
    headers: {
      Authorization: `Bearer ${auth?.token}`,
    },
  };

  const [loading, setLoading] = useState(false);

  const [banners, setBanners] = useState([]);

  const emptyForm = {
    title: "",
    subtitle: "",
    link: "",
    order: 0,
    active: true,
  };

  const [form, setForm] = useState(emptyForm);

  const [image, setImage] = useState(null);

  const [preview, setPreview] = useState("");

  const [editingBanner, setEditingBanner] = useState(null);

  // =============================
  // Get Banners
  // =============================

  const getBanners = async () => {
    try {
      setLoading(true);

      const { data } = await axios.get("/api/v1/banner/admin", headers);

      if (data.success) {
        setBanners(data.banners);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to load banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth?.token) {
      getBanners();
    }
  }, [auth]);

  // =============================
  // Image Select
  // =============================

  const handleImage = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setImage(file);

    setPreview(URL.createObjectURL(file));
  };

  // =============================
  // Reset Form
  // =============================

  const resetForm = () => {
    setForm(emptyForm);

    setImage(null);

    setPreview("");

    setEditingBanner(null);
  };
  // =============================
  // Create Banner
  // =============================

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!image && !editingBanner) {
        return toast.error("Please select a banner image");
      }

      const formData = new FormData();

      formData.append("title", form.title);
      formData.append("subtitle", form.subtitle);
      formData.append("link", form.link);
      formData.append("order", form.order);
      formData.append("active", form.active);

      if (image) {
        formData.append("image", image);
      }

      let response;

      if (editingBanner) {
        response = await axios.put(
          `/api/v1/banner/${editingBanner._id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}`,
              "Content-Type": "multipart/form-data",
            },
          },
        );
      } else {
   
        response = await axios.post("/api/v1/banner/create", formData, {
          headers: {
            Authorization: `Bearer ${auth?.token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data.success) {
        toast.success(
          editingBanner
            ? "Banner updated successfully"
            : "Banner created successfully",
        );

        resetForm();

        getBanners();
      }
    } catch (error) {
      console.log(error);

      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };

  // =============================
  // Edit Banner
  // =============================

  const handleEdit = (banner) => {
    setEditingBanner(banner);

    setForm({
      title: banner.title,
      subtitle: banner.subtitle,
      link: banner.link,
      order: banner.order,
      active: banner.active,
    });

    setPreview(`/${banner.image}`);

    setImage(null);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =============================
  // Delete Banner
  // =============================

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this banner?")) return;

    try {
      const { data } = await axios.delete(`/api/v1/banner/${id}`, headers);

      if (data.success) {
        toast.success("Banner deleted");

        getBanners();
      }
    } catch (error) {
      console.log(error);

      toast.error("Unable to delete banner");
    }
  };

  // =============================
  // Logout
  // =============================

  const handleLogout = () => {
    setAuth({
      user: null,
      token: "",
    });

    localStorage.removeItem("auth");

    navigate("/login");

    setTimeout(() => {
      window.location.reload();
    }, 100);
  };
  return (
    <Layout title="Banner Management">
      <div className="min-h-screen bg-gray-50 mt-20">
        <div className="max-w-[1600px] mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                Banner Management
              </h1>

              <p className="text-gray-500 mt-2">
                Manage homepage banners, offers and sale sliders.
              </p>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}

            <aside className="w-full lg:w-72">
              <Sidebar
                auth={auth}
                activeTab="banners"
                setActiveTab={() => {}}
                onLogout={handleLogout}
              />
            </aside>

            {/* Main */}

            <main className="flex-1">
              {/* Form */}

              <div className="bg-white rounded-2xl shadow-sm border p-8">
                <h2 className="text-2xl font-bold mb-6">
                  {editingBanner ? "Edit Banner" : "Upload Banner"}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Upload */}

                  <div>
                    <label className="block text-sm font-semibold mb-3">
                      Banner Image
                    </label>

                    <label className="border-2 border-dashed border-gray-300 rounded-xl h-64 flex flex-col items-center justify-center cursor-pointer hover:border-black transition">
                      {preview ? (
                        <img
                          src={preview}
                          alt=""
                          className="h-full w-full object-cover rounded-xl"
                        />
                      ) : (
                        <>
                          <FiUpload size={45} className="text-gray-400 mb-3" />

                          <p className="text-gray-500">
                            Click to Upload Banner
                          </p>
                        </>
                      )}

                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        onChange={handleImage}
                      />
                    </label>
                  </div>

                  {/* Fields */}

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Title
                      </label>

                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            title: e.target.value,
                          })
                        }
                        className="w-full border rounded-lg px-4 py-3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Subtitle
                      </label>

                      <input
                        type="text"
                        value={form.subtitle}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            subtitle: e.target.value,
                          })
                        }
                        className="w-full border rounded-lg px-4 py-3"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Redirect Link
                      </label>

                      <input
                        type="text"
                        placeholder="/products"
                        value={form.link}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            link: e.target.value,
                          })
                        }
                        className="w-full border rounded-lg px-4 py-3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Display Order
                      </label>

                      <input
                        type="number"
                        value={form.order}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            order: e.target.value,
                          })
                        }
                        className="w-full border rounded-lg px-4 py-3"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          active: e.target.checked,
                        })
                      }
                    />

                    <span>Banner Active</span>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      className="px-8 py-3 rounded-lg bg-black text-white font-semibold hover:bg-gray-800"
                    >
                      {editingBanner ? "Update Banner" : "Upload Banner"}
                    </button>

                    {editingBanner && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-8 py-3 rounded-lg border"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>

              {/* Banner List */}

              <div className="mt-8">
                <h2 className="text-2xl font-bold mb-6">All Banners</h2>

                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {banners.map((banner) => (
                    <div
                      key={banner._id}
                      className="bg-white rounded-xl border overflow-hidden shadow-sm flex flex-col h-full"
                    >
                      <img
                        src={`/${banner.image}`}
                        alt={banner.title}
                        className="h-48 w-full object-cover"
                      />

                      <div className="p-5 flex flex-col flex-1">
                        {/* Title */}
                        <h3
                          className="font-bold text-xl leading-tight line-clamp-1 min-h-[25px]"
                          title={banner.title}
                        >
                          {banner.title || "Untitled"}
                        </h3>

                        {/* Subtitle */}
                        <p
                          className="text-sm text-gray-500 mt-2 leading-6 line-clamp-3 min-h-[70px]"
                          title={banner.subtitle}
                        >
                          {banner.subtitle || "No subtitle available"}
                        </p>

                        {/* Status */}
                        <div className="flex items-center justify-between mt-5">
                          <span
                            className={`text-xs px-3 py-1 rounded-full ${
                              banner.active
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {banner.active ? "Active" : "Inactive"}
                          </span>

                          <span className="text-sm text-gray-500">
                            Order : {banner.order}
                          </span>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-3 mt-auto pt-6">
                          <button
                            onClick={() => handleEdit(banner)}
                            className="flex-1 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-100 transition flex items-center justify-center gap-2"
                          >
                            <FiEdit />
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(banner._id)}
                            className="flex-1 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white transition flex items-center justify-center gap-2"
                          >
                            <FiTrash2 />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};
export default AdminBanner;
